import { useState } from "react";

const RAPIDAPI_KEY = "8c4edac0b0msh5005849f5fe0680p155019jsn081b1f0a0a73";
const GROQ_API_KEY = "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";

const KEYWORDS = ["10ksquadnft", "puresoul", "10k squad", "10ksquad"];

const MENTION_TIERS = [
  {
    min: 50,
    name: "SQUAD LEGEND",
    emoji: "👑",
    image: "/images/legend.png",
    color: "#f59e0b",
    glow: "0 0 40px rgba(234,179,8,0.25), 0 0 80px rgba(234,179,8,0.1)",
    border: "1px solid rgba(234,179,8,0.5)",
    gradient: "linear-gradient(135deg, #f59e0b, #fbbf24)",
    message: "You ARE the 10K Squad. Absolute royalty.",
    sub: "The rarest of the rare. The entire collection bows.",
  },
  {
    min: 20,
    name: "INSANE HODLER",
    emoji: "🔥",
    image: "/images/insane.png",
    color: "#fb923c",
    glow: "0 0 40px rgba(249,115,22,0.25), 0 0 80px rgba(249,115,22,0.1)",
    border: "1px solid rgba(249,115,22,0.5)",
    gradient: "linear-gradient(135deg, #fb923c, #f97316)",
    message: "Absolutely unhinged.",
    sub: "We mean that as the highest compliment possible.",
  },
  {
    min: 10,
    name: "DIAMOND HANDS",
    emoji: "💎",
    image: "/images/diamond.png",
    color: "#22d3ee",
    glow: "0 0 40px rgba(34,211,238,0.25), 0 0 80px rgba(34,211,238,0.1)",
    border: "1px solid rgba(34,211,238,0.5)",
    gradient: "linear-gradient(135deg, #22d3ee, #06b6d4)",
    message: "Built different.",
    sub: "Never stops posting. Pure conviction.",
  },
  {
    min: 5,
    name: "PARROT GANG",
    emoji: "🦜",
    image: "/images/parrot.png",
    color: "#4ade80",
    glow: "0 0 40px rgba(74,222,128,0.25), 0 0 80px rgba(74,222,128,0.1)",
    border: "1px solid rgba(74,222,128,0.5)",
    gradient: "linear-gradient(135deg, #4ade80, #22c55e)",
    message: "Loyal to the flock.",
    sub: "The gang grows stronger with every mention.",
  },
  {
    min: 1,
    name: "SQUAD MEMBER",
    emoji: "🎯",
    image: "/images/member.png",
    color: "#e879f9",
    glow: "0 0 40px rgba(232,121,249,0.25), 0 0 80px rgba(232,121,249,0.1)",
    border: "1px solid rgba(232,121,249,0.5)",
    gradient: "linear-gradient(135deg, #e879f9, #d946ef)",
    message: "Welcome to the squad, fren.",
    sub: "This is just the beginning of your journey.",
  },
  {
    min: 0,
    name: "NGMI",
    emoji: "😢",
    image: "/images/ngmi.png",
    color: "#6b7280",
    glow: "0 0 20px rgba(107,114,128,0.15)",
    border: "1px solid rgba(107,114,128,0.3)",
    gradient: "linear-gradient(135deg, #6b7280, #9ca3af)",
    message: "You have zero mentions.",
    sub: "The keywords are right there ser... just saying.",
  },
];

function getTierByMentions(count: number) {
  return MENTION_TIERS.find((t) => count >= t.min) ?? MENTION_TIERS[MENTION_TIERS.length - 1];
}

type TweetResult = {
  text: string;
  created_at?: string;
};

type AnalysisResult = {
  username: string;
  totalTweets: number;
  mentionCount: number;
  matchedTweets: TweetResult[];
  squadScore: number;
  aiVerdict: string;
};

async function fetchUserTweets(username: string): Promise<TweetResult[]> {
  const res = await fetch(
    `https://twitter-api45.p.rapidapi.com/timeline.php?screenname=${encodeURIComponent(username)}`,
    {
      headers: {
        "x-rapidapi-key": RAPIDAPI_KEY,
        "x-rapidapi-host": "twitter-api45.p.rapidapi.com",
        "Content-Type": "application/json",
      },
    }
  );
  if (!res.ok) throw new Error(`Failed to fetch tweets: ${res.status}`);
  const data = await res.json();
  return data.timeline ?? data.tweets ?? data.data ?? [];
}

async function getGroqVerdict(
  username: string,
  mentionCount: number,
  totalTweets: number,
  matchedTweets: TweetResult[]
): Promise<{ score: number; verdict: string }> {
  const sampleTweets = matchedTweets
    .slice(0, 5)
    .map((t) => `- "${t.text}"`)
    .join("\n");

  const prompt = `You are rating a Twitter/X user's loyalty to the 10K Squad NFT community on Monad blockchain.

User: @${username}
Total tweets analyzed: ${totalTweets}
Mentions of 10ksquadnft or puresoul: ${mentionCount}
Sample matching tweets:
${sampleTweets || "None found"}

Based on this data, rate their Squad loyalty from 0-100.
Respond ONLY with valid JSON, no markdown, no backticks:
{"score": <number 0-100>, "verdict": "<1-2 punchy sentences about this user's squad energy>"}`;

  const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${GROQ_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model: "llama-3.3-70b-versatile",
      max_tokens: 150,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!res.ok) throw new Error(`Groq error: ${res.status}`);
  const data = await res.json();
  const text = data.choices?.[0]?.message?.content ?? "{}";
  const clean = text.replace(/```json|```/g, "").trim();
  return JSON.parse(clean);
}

function ScoreRing({ score, color }: { score: number; color: string }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  return (
    <div style={{ position: "relative", width: 128, height: 128 }}>
      <svg width="128" height="128" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="64" cy="64" r={r} fill="none" stroke="#1a1a2e" strokeWidth="10" />
        <circle
          cx="64" cy="64" r={r} fill="none"
          stroke={color} strokeWidth="10"
          strokeDasharray={`${fill} ${circ}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 1s ease" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 28, fontWeight: 800, color, fontFamily: "'Space Mono', monospace" }}>{score}</span>
        <span style={{ fontSize: 10, color: "#888", letterSpacing: 2, fontFamily: "monospace" }}>SCORE</span>
      </div>
    </div>
  );
}

function TierImage({ tier }: { tier: typeof MENTION_TIERS[0] }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "4rem" }}>
      {tier.emoji}
    </div>
  ) : (
    <img
      src={tier.image}
      alt={tier.name}
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
      onError={() => setFailed(true)}
    />
  );
}

function NextTierBar({ mentionCount, tier }: { mentionCount: number; tier: typeof MENTION_TIERS[0] }) {
  const tierIndex = MENTION_TIERS.findIndex((t) => mentionCount >= t.min);
  const nextTier = MENTION_TIERS[tierIndex - 1];

  if (!nextTier || mentionCount >= 50) {
    return (
      <p style={{ color: "#f59e0b", fontFamily: "monospace", fontSize: 11, textAlign: "center", opacity: 0.7, margin: 0 }}>
        👑 MAX TIER — You're at the top ser
      </p>
    );
  }

  const needed = nextTier.min - mentionCount;
  const progress = Math.min((mentionCount / nextTier.min) * 100, 100);

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontFamily: "monospace", color: "#555" }}>
        <span>{tier.name}</span>
        <span>{needed} more → {nextTier.emoji} {nextTier.name}</span>
      </div>
      <div style={{ width: "100%", height: 6, background: "#1a1a2e", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 99, background: tier.gradient, width: `${progress}%`, transition: "width 0.7s ease" }} />
      </div>
    </div>
  );
}

export default function SquadChecker() {
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const activeTier = result ? getTierByMentions(result.mentionCount) : null;

  async function handleCheck() {
    if (!username.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const handle = username.replace("@", "").trim();
      const tweets = await fetchUserTweets(handle);
      const matchedTweets = tweets.filter((t) => {
        const text = (t.text ?? "").toLowerCase();
        return KEYWORDS.some((kw) => text.includes(kw));
      });
      const mentionCount = matchedTweets.length;
      const { score, verdict } = await getGroqVerdict(handle, mentionCount, tweets.length, matchedTweets);

      setResult({
        username: handle,
        totalTweets: tweets.length,
        mentionCount,
        matchedTweets,
        squadScore: score,
        aiVerdict: verdict,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0a0008", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "48px 16px", fontFamily: "'Space Mono', 'Courier New', monospace", color: "#e0e0e0" }}>

      {/* Background blobs */}
      <div style={{ pointerEvents: "none", position: "fixed", inset: 0, overflow: "hidden", zIndex: 0 }}>
        <div style={{ position: "absolute", top: -160, left: -160, width: 384, height: 384, background: "rgba(219,39,119,0.08)", borderRadius: "50%", filter: "blur(48px)" }} />
        <div style={{ position: "absolute", top: "50%", right: -160, width: 320, height: 320, background: "rgba(147,51,234,0.06)", borderRadius: "50%", filter: "blur(48px)" }} />
      </div>

      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", gap: 24 }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(219,39,119,0.1)", border: "1px solid rgba(219,39,119,0.2)", color: "rgba(249,168,212,0.7)", fontSize: 11, padding: "6px 12px", borderRadius: 99, marginBottom: 12, letterSpacing: 2 }}>
            SQUAD LOYALTY CHECKER · MONAD
          </div>
          <div style={{ display: "inline-block", background: "linear-gradient(135deg, #00ff87, #00d4ff)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontSize: 36, fontWeight: 900, letterSpacing: -1, lineHeight: 1.1 }}>
            10K SQUAD
          </div>
          <div style={{ fontSize: 12, color: "#555", letterSpacing: 4, textTransform: "uppercase", marginTop: 4 }}>
            Mention Tier Checker
          </div>
        </div>

        {/* Input */}
        <div style={{ display: "flex", gap: 12, width: "100%" }}>
          <input
            type="text"
            placeholder="@username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCheck()}
            style={{ flex: 1, background: "rgba(219,39,119,0.05)", border: "1px solid rgba(219,39,119,0.2)", borderRadius: 12, color: "#e0e0e0", padding: "12px 16px", fontSize: 14, fontFamily: "inherit", outline: "none" }}
          />
          <button
            onClick={handleCheck}
            disabled={loading || !username.trim()}
            style={{ background: loading ? "#1a1a2e" : "linear-gradient(135deg, #00ff87, #00d4ff)", border: "none", borderRadius: 12, color: loading ? "#555" : "#000", padding: "12px 24px", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: loading ? "not-allowed" : "pointer", letterSpacing: 1 }}
          >
            {loading ? "SCANNING..." : "CHECK"}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "#1a0a0a", border: "1px solid #ff4444", borderRadius: 12, padding: "12px 20px", color: "#ff6666", fontSize: 12 }}>
            ⚠ {error}
          </div>
        )}

        {/* Result */}
        {result && activeTier && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeIn 0.4s ease" }}>

            {/* Tier card */}
            <div style={{ borderRadius: 20, border: activeTier.border, overflow: "hidden", boxShadow: activeTier.glow }}>
              <div style={{ height: 3, background: activeTier.gradient }} />
              <div style={{ background: "#0a0008", padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>

                {/* Address + badge */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontFamily: "monospace", fontSize: 11, color: "rgba(249,168,212,0.3)" }}>@{result.username}</span>
                  <span style={{ fontSize: 11, fontFamily: "monospace", padding: "3px 10px", borderRadius: 8, border: `1px solid ${activeTier.color}33`, background: `${activeTier.color}22`, color: activeTier.color }}>
                    {activeTier.emoji} {result.mentionCount} mentions
                  </span>
                </div>

                {/* Image + info */}
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div style={{ width: 120, height: 120, borderRadius: 16, overflow: "hidden", border: activeTier.border, flexShrink: 0, boxShadow: activeTier.glow }}>
                    <TierImage tier={activeTier} />
                  </div>
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: 2, color: activeTier.color }}>
                      {activeTier.name}
                    </div>
                    <div style={{ fontSize: 14, color: "#e0e0e0", fontWeight: 600 }}>{activeTier.message}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{activeTier.sub}</div>
                  </div>
                </div>

                {/* Score ring + verdict */}
                <div style={{ display: "flex", alignItems: "center", gap: 20, background: "#0d0d16", borderRadius: 12, padding: 16 }}>
                  <ScoreRing score={result.squadScore} color={activeTier.color} />
                  <div style={{ flex: 1, fontSize: 12, color: "#999", lineHeight: 1.7 }}>
                    <div style={{ fontSize: 10, color: "#444", letterSpacing: 2, marginBottom: 6 }}>AI VERDICT</div>
                    {result.aiVerdict}
                  </div>
                </div>

                {/* Next tier bar */}
                <NextTierBar mentionCount={result.mentionCount} tier={activeTier} />
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { label: "Tweets Scanned", value: result.totalTweets },
                { label: "Squad Mentions", value: result.mentionCount },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: "#111118", border: "1px solid #2a2a3a", borderRadius: 12, padding: "16px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: activeTier.color }}>{value}</div>
                  <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginTop: 2 }}>{label.toUpperCase()}</div>
                </div>
              ))}
            </div>

            {/* Matched tweets */}
            {result.matchedTweets.length > 0 && (
              <div style={{ background: "#111118", border: "1px solid #2a2a3a", borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#555", marginBottom: 12 }}>SQUAD MENTIONS FOUND</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {result.matchedTweets.slice(0, 5).map((t, i) => (
                    <div key={i} style={{ background: "#0d0d16", borderLeft: `2px solid ${activeTier.color}`, borderRadius: "0 8px 8px 0", padding: "10px 14px", fontSize: 12, color: "#bbb", lineHeight: 1.6 }}>
                      {t.text?.slice(0, 200)}{(t.text?.length ?? 0) > 200 ? "..." : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.mentionCount === 0 && (
              <div style={{ background: "#110a0a", border: "1px solid #2a1a1a", borderRadius: 12, padding: "16px 20px", fontSize: 12, color: "#886", textAlign: "center" }}>
                No mentions of <strong style={{ color: "#ff8c00" }}>10ksquadnft</strong> or{" "}
                <strong style={{ color: "#ff8c00" }}>puresoul</strong> found in recent tweets.
              </div>
            )}
          </div>
        )}

        {/* All tiers list */}
        <div>
          <p style={{ color: "rgba(249,168,212,0.2)", fontFamily: "monospace", fontSize: 11, letterSpacing: 4, textAlign: "center", marginBottom: 12 }}>ALL TIERS</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {MENTION_TIERS.map((t, i) => {
              const isYours = result !== null && getTierByMentions(result.mentionCount).name === t.name;
              return (
                <div
                  key={t.name}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 16px", borderRadius: 12,
                    border: isYours ? t.border : "1px solid rgba(219,39,119,0.1)",
                    background: isYours ? `${t.color}08` : "rgba(219,39,119,0.03)",
                    boxShadow: isYours ? t.glow : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  <span style={{ fontSize: "1.2rem", flexShrink: 0 }}>{t.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: isYours ? t.color : "rgba(249,168,212,0.3)", letterSpacing: 2 }}>{t.name}</p>
                    <p style={{ margin: 0, fontFamily: "monospace", fontSize: 10, color: "#444", marginTop: 2 }}>
                      {t.min === 0 ? "0 mentions" : t.min === 50 ? "50+ mentions" : `${t.min}–${MENTION_TIERS[i - 1].min - 1} mentions`}
                    </p>
                  </div>
                  {isYours && (
                    <span style={{ fontSize: 10, fontFamily: "monospace", color: t.color }}>▶ YOU</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap');
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        input:focus { border-color: rgba(0,255,135,0.5) !important; }
      `}</style>
    </div>
  );
}